"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Calendar, ArrowRight } from 'lucide-react';
function BlogArchiveContent() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    setPage
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-8">
          <div className="w-full h-[400px] bg-muted/20 animate-pulse rounded-lg"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[300px] bg-muted/20 animate-pulse rounded-lg"></div>)}
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-1">
          <div className="h-[200px] bg-muted/20 animate-pulse rounded-lg"></div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load the latest financial insights.</p>
        <Button onClick={() => window.location.reload()} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary/10">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p className="font-mono">No financial reports available at the moment. Check back soon!</p>
      </div>;
  }
  const featuredPost = posts[0];
  const gridPosts = posts.slice(1);
  const trendingTopics = ["Tax Strategy", "Cash Flow", "Bookkeeping", "Payroll", "Compliance", "Cloud Accounting", "Audit Ready", "SME Growth"];
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      items.push(<PaginationItem key="1" data-index="first">
          <PaginationLink onClick={() => setPage(1)} className="cursor-pointer hover:text-secondary">1</PaginationLink>
        </PaginationItem>);
      if (startPage > 2) {
        items.push(<PaginationItem key="ellipsis-start" data-index="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      items.push(<PaginationItem key={i} data-index={i}>
          <PaginationLink isActive={currentPage === i} onClick={() => setPage(i)} className={`cursor-pointer ${currentPage === i ? "bg-primary text-primary-foreground" : "hover:text-secondary"}`}>
            {i}
          </PaginationLink>
        </PaginationItem>);
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<PaginationItem key="ellipsis-end" data-index="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>);
      }
      items.push(<PaginationItem key={totalPages} data-index="last">
          <PaginationLink onClick={() => setPage(totalPages)} className="cursor-pointer hover:text-secondary">{totalPages}</PaginationLink>
        </PaginationItem>);
    }
    return items;
  };
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      
      <div className="lg:col-span-3 flex flex-col gap-8">
        
        
        {featuredPost && <article className="group relative overflow-hidden rounded-lg border border-border bg-card shadow-lg backdrop-blur-sm">
            <Link to={featuredPost.link || "#"} className="block absolute inset-0 z-10">
              <span className="sr-only">Read {featuredPost.title}</span>
            </Link>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="relative aspect-video md:aspect-auto h-full overflow-hidden bg-muted">
                {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-accent/20">
                    <span className="text-muted-foreground font-mono text-xs">DATA_NOT_FOUND</span>
                  </div>}
                {featuredPost.categories && featuredPost.categories.length > 0 && <div className="absolute top-4 left-4 z-20">
                    <Badge variant="default" className="bg-primary text-primary-foreground rounded-md font-default">
                      {featuredPost.categories[0].name}
                    </Badge>
                  </div>}
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground mb-4">
                  {featuredPost.date && <div className="flex items-center gap-1">
                      <SafeHydrate><Calendar className="w-3 h-3 text-secondary" /></SafeHydrate>
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                    </div>}
                </div>
                <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground mb-4 line-clamp-3 group-hover:text-secondary transition-colors tracking-tight">
                  {featuredPost.title}
                </h3>
                {featuredPost.excerpt && <div className="text-muted-foreground line-clamp-3 mb-6 font-default" dangerouslySetInnerHTML={{
              __html: featuredPost.excerpt
            }} />}
                <div className="mt-auto pt-4">
                  <span className="inline-flex items-center text-sm font-bold text-primary group-hover:text-secondary transition-colors underline-offset-4">
                    Analyze Full Report <ArrowRight className="ml-2 w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </article>}

        
        {gridPosts.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {gridPosts.map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden bg-card border-border hover:border-secondary/50 transition-all duration-300 hover:-translate-y-1 shadow-md h-full flex flex-col rounded-lg">
                <Link to={post.link || "#"} className="block relative aspect-[4/3] overflow-hidden bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-accent/10">
                      <span className="text-muted-foreground font-mono text-xs">IMG_NULL</span>
                    </div>}
                  {post.categories && post.categories.length > 0 && <div className="absolute top-3 left-3 z-10">
                      <Badge variant="secondary" className="bg-background/90 backdrop-blur-md text-foreground hover:bg-secondary hover:text-secondary-foreground rounded-md font-default">
                        {post.categories[0].name}
                      </Badge>
                    </div>}
                </Link>
                <CardContent className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground mb-3">
                    {post.date && <time dateTime={post.date}>{new Date(post.date).toLocaleDateString()}</time>}
                  </div>
                  <h4 className="text-lg font-default font-bold text-foreground mb-3 line-clamp-2 group-hover:text-secondary transition-colors tracking-tight">
                    <Link to={post.link || "#"}>{post.title}</Link>
                  </h4>
                  {post.excerpt && <div className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1 font-default" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                </CardContent>
              </Card>)}
          </div>}

        
        {totalPages > 1 && <div className="mt-12 border-t border-border pt-8">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious onClick={hasPrev ? prevPage : undefined} className={!hasPrev ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-accent rounded-md"} />
                </PaginationItem>
                
                {renderPaginationItems()}

                <PaginationItem>
                  <PaginationNext onClick={hasNext ? nextPage : undefined} className={!hasNext ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-accent rounded-md"} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>}
      </div>

      
      <aside className="hidden lg:block lg:col-span-1">
        <div className="sticky top-24 space-y-8">
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg backdrop-blur-sm">
            <h3 className="text-lg font-default font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-lg bg-secondary animate-pulse"></span>
              Market Intelligence
            </h3>
            <div className="flex flex-wrap gap-2">
              {trendingTopics.map((topic, i) => <Badge key={topic} data-index={i} variant="outline" className="cursor-pointer font-mono text-[10px] border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all py-1.5 px-3 rounded-md">
                  {topic}
                </Badge>)}
            </div>
          </div>

          <div className="bg-accent/30 border border-border rounded-lg p-6 text-center shadow-md">
            <h4 className="font-default font-bold text-foreground mb-2">Optimize Your Ledger</h4>
            <p className="text-sm text-muted-foreground mb-4 font-default">Get precision financial insights and tax updates delivered to your inbox.</p>
            <Button className="w-full bg-gradient-to-r from-primary to-secondary text-primary-foreground font-bold rounded-md hover:opacity-90 transition-opacity">
              Subscribe Now
            </Button>
          </div>
        </div>
      </aside>
    </div>;
}
export default function FinancialIntelligence() {
  const wp_query_params = {
    post_type: "post",
    per_page: 7,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="224531" id="financial-intelligence" className="bg-background text-foreground py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 md:mb-16">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tighter">
            Financial Intelligence
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl font-default">
            Strategic insights and professional guidance to help small businesses and startups master their financial landscape.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 7,
        status: ["publish"]
      }}>
          <BlogArchiveContent />
        </PostsProvider>
      </div>
    </section>;
}